import type { DaySummary, DiaryEntry, UnresolvedItem } from '@calorie/shared';
import { formatQuantity } from '@calorie/shared';
import { STAGE_LABELS, type PendingEntry } from '../lib/pending.js';
import { AppBar } from '../components/AppBar.js';
import { DaySummaryBar } from '../components/DaySummaryBar.js';
import { useSwipe } from '../lib/useSwipe.js';
import { WaterGlassIcon, WaterProgress } from '../components/WaterProgress.js';

interface Props {
  day: DaySummary;
  today: string;
  /** Отправленные записи, которые сервер ещё считает. */
  pending: PendingEntry[];
  onDismissPending: (id: string) => void;
  /** Позиции, которые сервер не смог опознать в последней записи. */
  unresolved: UnresolvedItem[];
  onDismissUnresolved: () => void;
  onDelete: (entryId: string) => void;
  /** Тап по калориям записи - разбор «из чего сложилось». */
  onOpenDetails: (entry: DiaryEntry) => void;
  onSettings: () => void;
  /** −1 - предыдущий день, +1 - следующий. */
  onShiftDay: (days: number) => void;
  onToday: () => void;
}

export function Today({
  day,
  today,
  pending,
  onDismissPending,
  unresolved,
  onDismissUnresolved,
  onDelete,
  onOpenDetails,
  onSettings,
  onShiftDay,
  onToday,
}: Props) {
  // Как перелистывание страниц: тянем содержимое вправо - открывается то,
  // что «левее», то есть вчера. Влево - вперёд, к завтра.
  const swipe = useSwipe({
    onLeft: () => onShiftDay(1),
    onRight: () => onShiftDay(-1),
  });

  return (
    <div className="app" {...swipe}>
      {/* Шапка и итог дня закреплены вместе: сводка видна на любой прокрутке */}
      <div className="appbar-stack">
        <AppBar
          date={day.date}
          today={today}
          streak={day.streak}
          onToday={onToday}
          onSettings={onSettings}
        />
        <DaySummaryBar day={day} />
        <WaterProgress water={day.water} />
      </div>

      {unresolved.length > 0 && (
        <div className="card unresolved">
          <div className="unresolved-head">
            <span>Не разобрал</span>
            <button className="icon-btn" onClick={onDismissUnresolved} aria-label="Скрыть">
              ✕
            </button>
          </div>
          {unresolved.map((item) => (
            <div className="unresolved-item" key={item.text}>
              <span className="unresolved-text">«{item.text}»</span>
              <span className="feed-sub">{item.reason}</span>
            </div>
          ))}
          <p className="hint" style={{ margin: '8px 0 0' }}>
            Попробуйте написать проще - например «овсянка 100 г».
          </p>
        </div>
      )}

      {day.entries.length === 0 && pending.length === 0 ? (
        <div className="empty">
          {day.date === today
            ? 'Сегодня пока пусто. Напишите внизу, что вы съели - например «гречка 200 г, куриная грудка 150 г».'
            : 'В этот день ничего не отмечено.'}
        </div>
      ) : (
        <ul className="feed">
          {day.entries.map((entry) => {
            const isWater = entry.items.length > 0 && entry.items.every((item) => item.kind === 'water');
            const waterMl = isWater ? entry.items.reduce((sum, item) => sum + (item.volumeMl ?? 0), 0) : 0;
            return (
            <li className={`feed-row${isWater ? ' feed-row-water' : ''}`} key={entry.id}>
              {isWater && <span className="feed-water-icon"><WaterGlassIcon /></span>}
              <div className="feed-body">
                <span className="feed-name">{entry.text}</span>
                <span className="feed-sub">
                  {isWater ? `${Math.round(waterMl)} мл` : entry.items.map((i) => `${i.name} ${formatQuantity(i)}`).join(' · ')}
                </span>
              </div>
              {isWater ? <span className="feed-water-amount">+{Math.round(waterMl)}<i>мл</i></span> : <button
                className="feed-kcal feed-kcal-btn"
                onClick={() => onOpenDetails(entry)}
                aria-label={`Из чего сложились калории: ${entry.text}`}
              >
                {Math.round(entry.kcal)}
                <i>ккал</i>
              </button>}
              <button
                className="feed-remove"
                aria-label={`Удалить ${entry.text}`}
                onClick={() => onDelete(entry.id)}
              >
                ✕
              </button>
            </li>);
          })}
        </ul>
      )}

      {pending.length > 0 && (
        <ul className="feed">
          {pending.map((item) => (
            <li className="feed-row" key={item.id}>
              <div className="feed-body">
                <span className="feed-name">{item.text}</span>
              </div>
              {item.stage === 'failed' ? (
                <>
                  <span className="feed-stage failed">{STAGE_LABELS.failed}</span>
                  <button
                    className="feed-remove"
                    aria-label="Убрать"
                    onClick={() => onDismissPending(item.id)}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <span className="feed-stage">{STAGE_LABELS[item.stage]}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
